// FIRST thing the app runs. Imported at the top of index.js, before anything
// else, because ES imports are evaluated in source order and reownConfig.js
// calls createAppKit() at import time — wagmi reads its cookie storage right
// there. Cleaning after that point is already too late: AppKit has booted with
// the stale state. That is why the old clearReownCache(), which ran in the body
// of index.js, never actually prevented anything.
//
// What we learned the hard way: Ctrl+Shift+R fetches a fresh bundle but leaves
// localStorage, cookies and IndexedDB untouched — and the thing that wedges the
// login is precisely that leftover wallet state, at the SAME app version. So
// the trigger cannot be a version check. It is: "no valid session? then there
// is nothing worth keeping — start clean."

import { DASHBOARD_VERSION } from "./appVersion";

// wagmi's connector state lives in COOKIES (reownConfig hands it cookieStorage),
// the rest is spread across localStorage under these prefixes.
const WALLET_KEY = /(^wc@)|(^@w3m)|(^W3M)|appkit|walletconnect|reown|wagmi|w3m/i;

const safe = (fn) => {
  try {
    return fn();
  } catch (e) {
    console.warn("[bootClean] step skipped:", e && e.message);
    return undefined;
  }
};

const cookieDomains = () => {
  const host = window.location.hostname;
  const domains = [undefined, host, `.${host}`];
  const parts = host.split(".");
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);
  return domains;
};

/** Expire cookies whose name matches `pattern` (all of them when omitted). */
export const clearCookies = (pattern) => {
  const domains = cookieDomains();
  const killed = [];
  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0].trim();
    if (!name) return;
    if (pattern && !pattern.test(name)) return;
    killed.push(name);
    domains.forEach((domain) => {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : "");
    });
  });
  if (killed.length) console.log("[bootClean] Cleared cookies:", killed);
  return killed.length;
};

/** Remove wallet-related localStorage keys, leaving the session alone. */
export const clearWalletLocalStorage = () => {
  const doomed = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && WALLET_KEY.test(key)) doomed.push(key);
  }
  doomed.forEach((k) => localStorage.removeItem(k));
  if (doomed.length) console.log("[bootClean] Cleared wallet keys:", doomed);
  return doomed.length;
};

const hasValidSession = () => {
  const hash = localStorage.getItem("hash");
  const username = localStorage.getItem("username");
  const expiry = localStorage.getItem("expirationTime");
  return Boolean(hash && username && expiry && Date.now() < parseInt(expiry, 10));
};

// ---------------------------------------------------------------------------
// Decide what this page load needs, and do the SYNCHRONOUS half of it now.
// index.js finishes the asynchronous half (IndexedDB, Cache API, service
// workers) once the module graph has loaded.
// ---------------------------------------------------------------------------

let mode = "none";

safe(() => {
  const forced = new URLSearchParams(window.location.search).has("reset");
  const versionChanged = localStorage.getItem("dashboardVersion") !== DASHBOARD_VERSION;

  if (forced || versionChanged) {
    // Full wipe: an explicit support reset, or a new deploy.
    mode = forced ? "forced" : "version";
    localStorage.clear();
    sessionStorage.clear();
    clearCookies();
    // Keep the wallet auto-login guard armed so an injected wallet cannot
    // silently re-authenticate straight after a wipe.
    sessionStorage.setItem("manualLogout", "true");
    localStorage.setItem("dashboardVersion", DASHBOARD_VERSION);
  } else if (!hasValidSession()) {
    // The everyday case, and the one that actually fixes partners: nobody is
    // logged in, so any wallet state still lying around is from a dead session
    // and can only cause the "modal opens and does nothing" symptom. Clear it
    // on EVERY logged-out load — no version bump needed, no reload needed.
    mode = "walletOnly";
    clearWalletLocalStorage();
    clearCookies(WALLET_KEY);
  }
});

console.log(`[bootClean] mode=${mode} version=${DASHBOARD_VERSION}`);

export const bootCleanMode = mode;
export default mode;
